import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/app/components/ui/dialog';
import { Button } from '@/app/components/ui/button';
import { Label } from '@/app/components/ui/label';
import { Slider } from '@/app/components/ui/slider';
import { ViewDisplaySettings } from '@/app/types/cad';

interface ViewSettingsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  settings: ViewDisplaySettings;
  onSettingsChange: (settings: ViewDisplaySettings) => void;
}

export function ViewSettingsDialog({
  open,
  onOpenChange,
  settings,
  onSettingsChange,
}: ViewSettingsDialogProps) {
  const handleReset = () => {
    onSettingsChange({
      lineWidthMultiplier: 1.0,
      connectionSizeMultiplier: 1.0,
      fontSizeMultiplier: 1.0,
      iconSizeMultiplier: 1.0,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Ansichtseinstellungen</DialogTitle>
          <DialogDescription>
            Globale Einstellungen für die Darstellung von Grafiken, Verbindungen und Text
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-6 py-4">
          {/* Line Width Multiplier */}
          <div className="grid gap-3">
            <div className="flex items-center justify-between">
              <Label htmlFor="lineWidth">Linienstärke</Label>
              <span className="text-sm text-muted-foreground">
                {settings.lineWidthMultiplier.toFixed(1)}x
              </span>
            </div>
            <Slider
              id="lineWidth"
              min={0.1}
              max={5.0}
              step={0.1}
              value={[settings.lineWidthMultiplier]}
              onValueChange={([value]) =>
                onSettingsChange({ ...settings, lineWidthMultiplier: value })
              }
              className="w-full"
            />
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>0.1x (dünn)</span>
              <span>1.0x (normal)</span>
              <span>5.0x (dick)</span>
            </div>
          </div>

          {/* Connection Size Multiplier */}
          <div className="grid gap-3">
            <div className="flex items-center justify-between">
              <Label htmlFor="connectionSize">Verbindungspunkte</Label>
              <span className="text-sm text-muted-foreground">
                {settings.connectionSizeMultiplier.toFixed(1)}x
              </span>
            </div>
            <Slider
              id="connectionSize"
              min={0.1}
              max={5.0}
              step={0.1}
              value={[settings.connectionSizeMultiplier]}
              onValueChange={([value]) =>
                onSettingsChange({ ...settings, connectionSizeMultiplier: value })
              }
              className="w-full"
            />
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>0.1x (klein)</span>
              <span>1.0x (normal)</span>
              <span>5.0x (groß)</span>
            </div>
          </div>

          {/* Font Size Multiplier */}
          <div className="grid gap-3">
            <div className="flex items-center justify-between">
              <Label htmlFor="fontSize">Schriftgröße</Label>
              <span className="text-sm text-muted-foreground">
                {settings.fontSizeMultiplier.toFixed(1)}x
              </span>
            </div>
            <Slider
              id="fontSize"
              min={0.1}
              max={5.0}
              step={0.1}
              value={[settings.fontSizeMultiplier]}
              onValueChange={([value]) =>
                onSettingsChange({ ...settings, fontSizeMultiplier: value })
              }
              className="w-full"
            />
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>0.1x (klein)</span>
              <span>1.0x (normal)</span>
              <span>5.0x (groß)</span>
            </div>
          </div>

          {/* Icon Size Multiplier */}
          <div className="grid gap-3">
            <div className="flex items-center justify-between">
              <Label htmlFor="iconSize">Icongröße</Label>
              <span className="text-sm text-muted-foreground">
                {(settings.iconSizeMultiplier ?? 1.0).toFixed(1)}x
              </span>
            </div>
            <Slider
              id="iconSize"
              min={0.1}
              max={5.0}
              step={0.1}
              value={[settings.iconSizeMultiplier ?? 1.0]}
              onValueChange={([value]) =>
                onSettingsChange({ ...settings, iconSizeMultiplier: value })
              }
              className="w-full"
            />
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>0.1x (klein)</span>
              <span>1.0x (normal)</span>
              <span>5.0x (groß)</span>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleReset}>
            Zurücksetzen
          </Button>
          <Button onClick={() => onOpenChange(false)}>Schließen</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}